import { track } from "@amplitude/analytics-react-native";
import { ReactNode, useMemo } from "react";
import { View } from "react-native";
import BottomPopInMenu from "src/components/BottomPopInMenu";
import FloatingMenuButton, {
	FLOATING_MENU_ITEM_WIDTH,
} from "src/components/FloatingMenuButton/FloatingMenuButton";
import MultiStageToggle from "src/components/MultiStageToggle";
import Noise from "src/components/Noise/Noise";
import RoundedButton from "src/components/RoundedButton";
import useDevice from "src/hooks/useDevice";
import useDim from "src/hooks/useDim";
import IconAddPhoto from "src/icons/IconAddPhoto";
import IconAlbum from "src/icons/IconAlbum";
import IconFire from "src/icons/IconFire";
import { paddingLarge, paddingMedium, paddingSmall } from "src/styles/spacing";
import { BlurView } from "src/swsh-native";
import useGatheringPublicFullContext from "../../hooks/useGatheringPublicFullContext";
import { useGatheringPublicFullStore } from "../../hooks/useGatheringPublicFullStore";

const BUBBLE_HORIZONTAL_MARGIN = paddingMedium;
const GatheringPublicFullFooter = () => {
	const photosMode = useGatheringPublicFullStore((state) => state.photosMode);
	const hasTopPhotos = useGatheringPublicFullStore((state) => state.hasTopPhotos);
	const { store } = useGatheringPublicFullContext();
	const dim = useDim();
	const privacy = useGatheringPublicFullStore((state) => state.privacy);
	const setPopUpVariant = useGatheringPublicFullStore((state) => state.setPopUpVariant);

	const items = useMemo(() => {
		const items: {
			node: ReactNode;
			name: "Top" | "All";
		}[] = [];
		if (hasTopPhotos) {
			items.push({
				node: (
					<FloatingMenuButton
						label="Top"
						key="Top"
						testID="FloatingMenuButton-Top"
						onPress={() => {
							track("gathering_publicFull_topPhotos");
							store.setState({
								photosMode: "Top",
							});
						}}
					>
						<IconFire size={28} />
					</FloatingMenuButton>
				),
				name: "Top",
			});
		}
		items.push({
			node: (
				<FloatingMenuButton
					label="All"
					key="All"
					testID="FloatingMenuButton-All"
					onPress={() => {
						track("gathering_publicFull_allPhotos");
						store.setState({
							photosMode: "All",
						});
					}}
				>
					<IconAlbum size={28} />
				</FloatingMenuButton>
			),
			name: "All",
		});

		return items;
	}, [hasTopPhotos, store]);
	const selectedIndex = useMemo(() => {
		return items.findIndex((item) => item.name === photosMode) ?? 0;
	}, [items, photosMode]);
	const { device } = useDevice();

	return (
		<>
			<BottomPopInMenu weight={1000} group="GatheringFullPublicPreviewMenu">
				<View
					style={{
						width: dim.width,
						alignItems: "center",
						justifyContent: "center",
						flexDirection: "row",
					}}
				>
					{dim.width > 300 && device === "mobile" && (
						<RoundedButton
							Icon={IconAddPhoto}
							sizeVariant="CircleLarge"
							onPress={() => {
								track("gathering_publicFull_uploadPress");
								setPopUpVariant("UploadPhotos");
							}}
							containerStyle={[
								{
									position: "absolute",
									right: paddingLarge,
								},
							]}
						/>
					)}
					<BlurView
						testID={`GatheringPublicFullFooter`}
						style={{
							flexDirection: "row",
							alignItems: "center",
							borderRadius: 40,
							overflow: "hidden",
							paddingHorizontal: paddingSmall,
							paddingVertical: paddingSmall,
						}}
						intensity="floating-menu"
					>
						<Noise
							opacity={0.1}
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
							}}
						/>
						<View
							style={{
								width: items.length * FLOATING_MENU_ITEM_WIDTH,
								marginHorizontal: BUBBLE_HORIZONTAL_MARGIN,
								flexDirection: "row",
								justifyContent: "center",
							}}
						>
							<MultiStageToggle
								selectBubbleStyle={{
									width: "100%",
									borderRadius: 40,
								}}
								bubbleWidth={
									FLOATING_MENU_ITEM_WIDTH + 2 * BUBBLE_HORIZONTAL_MARGIN
								}
								selectedIndex={selectedIndex}
								Components={items.map((item) => item.node)}
							/>
						</View>
					</BlurView>
				</View>
			</BottomPopInMenu>
		</>
	);
};
export default GatheringPublicFullFooter;
